import { JSX, useMemo, useRef, useState } from "react";

import { Store, LocalMall, Train, Search } from "@mui/icons-material";
import { Autocomplete, TextField, Box, InputAdornment, AutocompleteRenderGroupParams, Paper, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { UserAppRole } from "src/constants/userRoles.enum";
import { getAppRolesFromUser } from "src/lib/user.helper";
import LocationType from "src/types/LocationType";

import { LocationSearchProps, AutocompleteOption, groupLocationOptions, filterOptions } from "./locationSearch.helper";
import { boxWidthSx, getOptionStyle, minHeight, StyledPopper, getStyles } from "./locationSearch.styles";

const searchAdornment = (
  <InputAdornment position='start'>
    <Search />
  </InputAdornment>
);

function LocationSearch({ selectedLocation, locationOptions, handleSelectedLocation }: LocationSearchProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const auth = useAuth();
  const userAppRoles = useMemo(() => getAppRolesFromUser(auth.user), [auth.user]);

  const groupedOptions = useMemo(() => {
    const groups = groupLocationOptions(locationOptions);
    const flatOptions: AutocompleteOption[] = [];

    for (const group of groups) {
      if (!group.region.isVirtualRegion) {
        flatOptions.push({
          title: `region-${group.region.id}`,
          id: group.region.id,
          label: group.region.label,
          type: group.type,
          isClickable: true,
          region: group.region.region,
          isVirtualRegion: group.region.isVirtualRegion,
        });
      }

      flatOptions.push(...group.children);
    }

    return flatOptions;
  }, [locationOptions]);

  const handleChange = (_: unknown, newValue: AutocompleteOption | null): void => {
    if (newValue?.isClickable) {
      const newSelectedLocation = locationOptions.find((l) => l.Id === newValue.id);
      if (newSelectedLocation && selectedLocation?.Id !== newSelectedLocation.Id) {
        const isRegion = newValue.title?.startsWith("region-");
        handleSelectedLocation(newSelectedLocation, newValue.type, isRegion);
      }
      setOpen(false);
    }
  };

  const renderGroup = (params: AutocompleteRenderGroupParams): JSX.Element | undefined => {
    if (
      (userAppRoles.includes(UserAppRole.ShopViewer) && params.group === t("Shops")) ||
      (userAppRoles.includes(UserAppRole.MallViewer) && params.group === t("Malls")) ||
      (userAppRoles.includes(UserAppRole.StationViewer) && params.group === t("Stations"))
    ) {
      const icon =
        params.group === t("Shops") ? (
          <Store sx={getStyles(theme).icon} />
        ) : params.group === t("Stations") ? (
          <Train sx={getStyles(theme).icon} />
        ) : (
          <LocalMall sx={getStyles(theme).icon} />
        );
      return (
        <div key={String(params.key)}>
          <Box sx={getStyles(theme).borderStyle}>
            <Box sx={getStyles(theme).groupHeaderStyle}>
              {icon}
              <Box component='span' sx={getStyles(theme).groupName}>
                {params.group}
              </Box>
            </Box>
            {params.children}
          </Box>
        </div>
      );
    }
  };

  return (
    <Box sx={boxWidthSx} height={36.5}>
      <Autocomplete
        ref={autocompleteRef}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableCloseOnSelect
        PopperComponent={StyledPopper}
        PaperComponent={(props) => <Paper {...props} sx={getStyles(theme).paper} />}
        onChange={handleChange}
        popupIcon={null}
        noOptionsText={t("No results found")}
        options={groupedOptions}
        filterOptions={filterOptions}
        groupBy={(option) => {
          const typeKey = LocationType[option.type];
          return t(`${typeKey}s`);
        }}
        getOptionKey={(option) => option.id}
        loading={!groupedOptions || groupedOptions.length === 0}
        renderGroup={renderGroup}
        renderOption={(props, option) => {
          const { key, ...restProps } = props;

          if (option?.title?.startsWith("region-")) {
            return (
              <Box component='li' key={key} {...restProps} sx={minHeight}>
                <Box sx={getStyles(theme).regionHeaderStyle}>
                  <Box component='span' sx={getStyles(theme).groupName}>
                    {option.label}
                  </Box>
                </Box>
              </Box>
            );
          }

          const icon =
            option.type === LocationType.Shop ? (
              <Store sx={getStyles(theme).icon} />
            ) : option.type === LocationType.Station ? (
              <Train sx={getStyles(theme).icon} />
            ) : (
              <LocalMall sx={getStyles(theme).icon} />
            );
          return (
            <Box component='li' key={`${key}-${option.type}`} {...restProps} sx={getOptionStyle(option.isClickable, theme)}>
              {icon}
              <span>{option.label}</span>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            sx={getStyles(theme).textField}
            size='small'
            fullWidth
            placeholder={t("search")}
            variant='outlined'
            // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
            InputProps={{
              ...params.InputProps,
              startAdornment: searchAdornment,
              sx: { height: 36.5 },
            }}
          />
        )}
      />
    </Box>
  );
}

export default LocationSearch;
